import React, {useState} from 'react';
import * as Icon from 'react-feather';
import {useSelector} from 'react-redux';
import {useAppDispatch} from '../../hooks/redux';

import {
    Input as AlchemyInput,
    Checkbox as AlchemyCheck,
    Button as AlchemyButton,
    Textarea as AlchemyTextarea,
} from '@amzn/alchemy-components-react';

// Import CSS module
import styles from './CreateBookmarkModal.module.css';

// Import from slices instead of actions
import { addBookmarkCard } from '../../slices/bookmarkCardsSlice';
import { closeCreateBookmark } from '../../slices/bookmarkModalSlice';
import { setFavourites as setMyFavouriteItems } from '../../slices/myFavouritesSlice';
import { setFavourites as setSiteFavouriteItems } from '../../slices/siteFavouritesSlice';
import { addMessage } from '../../slices/messageQueueSlice';
import { BookmarksApiService } from '../../api';
import { FavouritesApiService } from '../../api';
// Actions
import i18n from "../../i18n";
import { RootState } from '../../types';

export default function() {
    // load user detail here
    const currentUser = useSelector((state: RootState) => state.user);
    // load modal status here
    const isVisible = useSelector((state: RootState) => state.bookmarkModal.createBookmark.visible);
    const dispatch = useAppDispatch();
    // user input handling
    const [title, setTitle] = useState('');
    const [url, setUrl] = useState('');
    const [urlInputStatus, setUrlInputStatus] = useState('')
    const [isUrlNotValidated, setIsUrlNotValidated] = useState(true)
    const [isTitleNotValidated, setIsTitleNotValidated] = useState(true)
    const [description, setDescription] = useState('');
    const [addToMyFav, setAddToMyFav] = useState(true);
    const [addToSiteFav, setAddToSiteFav] = useState(false);

    const bmkApi = new BookmarksApiService()
    const favApi = new FavouritesApiService()

    const removeSpaces = (url:string) => {
        if (url.startsWith(' ') || url.endsWith(' ')) {
            url = url.trim()
            setUrl(url)
        }
        return url
    }

    const handleUrlInput = (userInput: string) => {
        const regEx = new RegExp('^https{0,1}:\\/\\/.*?')
        userInput = removeSpaces(userInput)
        if (regEx.test(userInput)) {
            setIsUrlNotValidated(false)
            setUrlInputStatus('')
        } else {
            setUrlInputStatus(i18n.t('modal:invalid-url-info'))
            setIsUrlNotValidated(true)
        }
        setUrl(userInput)
    }

    const handleTitleInput = (userInput: string) => {
        if (userInput.length !== 0) {
            setIsTitleNotValidated(false)
            setTitle(userInput)
        } else {
            setIsTitleNotValidated(true)
        }
    }

    const handleSubmit = () => {
        async function addBookmark() {
            try {
                // Add the bookmark
                const r1: any = await bmkApi.addBookmark(title, url, description);
                
                // Validate bookmark data exists
                if (!r1 || !r1.data) {
                    throw new Error('Invalid bookmark data received');
                }
                
                const bookmarkData = r1.data;
                const bookmarkId = bookmarkData.entity;
                
                // dispatch success message
                dispatch(addMessage({
                    id: Date.now().toString(),
                    icon: "add",
                    title: i18n.t('toast:bookmark-added'),
                    text: bookmarkData.title,
                    duration: 5000
                }));
                
                // Handle add into user's favorites
                if (addToMyFav) {
                    try {
                        const addMyFavResponse: any = await favApi.addToEntityFav(currentUser.login, bookmarkId, 'personal');
                        
                        if (addMyFavResponse && addMyFavResponse.favorite_bookmarks) {
                            const myNewFav = addMyFavResponse.favorite_bookmarks;
                            console.log(`addToMyFav:${JSON.stringify(myNewFav)}`)
                            // Update the myFavourites slice
                            dispatch(setMyFavouriteItems(myNewFav));
                            
                            // dispatch success message
                            dispatch(addMessage({
                                id: Date.now().toString(),
                                icon: "add",
                                title: i18n.t('toast:added-for-you'),
                                text: bookmarkData.title,
                                duration: 5000
                            }));
                        } else {
                            throw new Error('Invalid favorite data received');
                        }
                    } catch (error) {
                        console.error('Failed to add to personal favorites:', error);
                        dispatch(addMessage({
                            id: Date.now().toString(),
                            icon: "alert-triangle",
                            title: i18n.t('toast:add-to-favorites-failed'),
                            text: bookmarkData.title,
                            duration: 5000
                        }));
                    }
                }

                // Handle add into site favorites
                if (addToSiteFav) {
                    try {
                        const addMySiteFavResponse: any = await favApi.addToEntityFav(currentUser.whid, bookmarkId, 'site');
                        
                        if (addMySiteFavResponse && addMySiteFavResponse.favorite_bookmarks) {
                            const mySiteNewFav = addMySiteFavResponse.favorite_bookmarks;
                            console.log(`addToSiteFav:${JSON.stringify(mySiteNewFav)}`)
                            // Update the siteFavourites slice
                            dispatch(setSiteFavouriteItems(mySiteNewFav));
                            
                            // dispatch success message
                            dispatch(addMessage({
                                id: Date.now().toString(),
                                icon: "add",
                                title: `${i18n.t('toast:added-for')} ${currentUser.whid}`,
                                text: bookmarkData.title,
                                duration: 5000
                            }));
                        } else {
                            throw new Error('Invalid site favorite data received');
                        }
                    } catch (error) {
                        console.error('Failed to add to site favorites:', error);
                        dispatch(addMessage({
                            id: Date.now().toString(),
                            icon: "alert-triangle",
                            title: `${i18n.t('toast:add-to-site-failed')} ${currentUser.whid}`,
                            text: bookmarkData.title,
                            duration: 5000
                        }));
                    }
                }

                // Update state bookmarkcard.raw
                dispatch(addBookmarkCard(bookmarkData));
                
                // Close modal only when bookmark creation is successful
                resetModlaValue();
                
            } catch (error) {
                console.error('Failed to create bookmark:', error);
                dispatch(addMessage({
                    id: Date.now().toString(),
                    icon: "alert-triangle",
                    title: i18n.t('toast:bookmark-add-failed'),
                    text: title,
                    duration: 5000
                }));
                // Don't close the modal on error to allow the user to try again
            }
        }
        addBookmark();
    }

    const isNotvalidatedForSubmit = () => {
        let result = true
        const validatedConditions = [isUrlNotValidated, isTitleNotValidated]
        if (validatedConditions.every(v => v === false)) {
            result = false
        }

        return result
    }

    // TODO: should be able to replace by hook
    const resetModlaValue = () => {
        setUrl('')
        setTitle('')
        setDescription('')
        setIsUrlNotValidated(true)
        setIsTitleNotValidated(true)
        setAddToMyFav(true)
        setAddToSiteFav(false)
        dispatch(closeCreateBookmark());
    }

    const onCancel = () => {
        resetModlaValue()
    }

    const titleLabel = isTitleNotValidated ? `${i18n.t('universal:title')} (${i18n.t('modal:required')})` : i18n.t('universal:title')

    const modalClassName = isVisible 
      ? `${styles.bookmarkModal} ${styles.displayBlock}` 
      : `${styles.bookmarkModal} ${styles.displayNone}`;
    
    return (
        isVisible &&
        <div className={modalClassName}>
            <div className={styles.modalMain}>
                <div className={styles.modalHeader}>
                    <div className={styles.headerTitle}>
                        <Icon.Bookmark size={20} />
                        <span>{i18n.t('button:new-bookmark')}</span>
                    </div>
                    <div className={styles.closeButton} onClick={onCancel}>
                        <Icon.X size={20} />
                    </div>
                </div>
                <div className={styles.modalBody}>
                    <div>
                        <div className={styles.formField}>
                            <AlchemyInput name="Url"
                                label={i18n.t('universal:url')}
                                placeholder={i18n.t('modal:input-url')}
                                onInput = {(e) => handleUrlInput(e.target.value)}
                                value = {url}
                                status = {urlInputStatus}
                                required
                                style={{ width: '100%' }}
                            />
                        </div>
                        <div className={styles.formField}>
                            <AlchemyInput name="Title"
                                label={titleLabel}
                                placeholder={i18n.t('modal:input-title')}
                                onInput = {(e) => handleTitleInput(e.target.value)}
                                disabled = {isUrlNotValidated}
                                maxlength={40}
                                required
                                style={{ width: '100%' }}
                            />
                        </div>
                        <div className={styles.formField}>
                            <AlchemyTextarea name="Description"
                                label = {i18n.t('universal:description')}
                                placeholder={i18n.t('modal:input-description')}
                                rows={2}
                                notResizable={true}
                                maxlength={160}
                                onInput = {(e) => setDescription(e.target.value)}
                                disabled = {isUrlNotValidated}
                                style={{ width: '100%' }}
                            />
                        </div>
                        <div className={styles.checkboxField}>
                            <AlchemyCheck name="AddToMyFav"
                                checked = {addToMyFav}
                                label = {`${i18n.t('universal:add-new-bookmark-to')} ${currentUser.fullName}`}
                                onChange = {(e) => setAddToMyFav(e.target.checked)}
                                disabled = {isUrlNotValidated}
                            />
                        </div>
                        <div>
                            <AlchemyCheck name="AddToSiteFav"
                                checked = {addToSiteFav}
                                label = {`${i18n.t('universal:add-new-bookmark-to')} ${currentUser.whid}`}
                                onChange = {(e) => setAddToSiteFav(e.target.checked)}
                                disabled = {isUrlNotValidated}
                            />
                        </div>
                    </div>
                </div>
                <div className={styles.modalFooter}>
                    <AlchemyButton onClick={onCancel}>{i18n.t('universal:cancel')}</AlchemyButton>
                    <AlchemyButton variant="action" onClick={handleSubmit} disabled={isNotvalidatedForSubmit()}>{i18n.t('universal:add')}</AlchemyButton>
                </div>
            </div>
        </div>
    )
}
